import { Theme, useTheme } from '@mui/material';
import { ConversationStatus } from '@provision/entity-types';
import { gql, QueryHookOptions, useQuery } from '@provision/graphql-client';
import { useMemo } from 'react';
import { QueryVariables, Records } from 'src/graphql/types';
import { getThemeColor } from 'src/utils/strings';

interface GetConversationStatusQuery {
  getConversationStatus: Records<ConversationStatus>;
}

const GET_CONVERSATION_STATUS_QUERY = gql`
  query GetConversationStatus($params: QueryParams) @entity(name: "ConversationStatus") {
    getConversationStatus(queryParams: $params) {
      records {
        id
        name
        sortOrder
      }
    }
  }
`;

export const useConversationStatusList = (
  options?: QueryHookOptions<GetConversationStatusQuery>,
) => {
  const { data, ...rest } = useQuery<GetConversationStatusQuery, QueryVariables>(
    GET_CONVERSATION_STATUS_QUERY,
    {
      ...options,
      variables: {
        params: {
          sorting: { attribute: 'sortOrder', desc: false },
        },
      },
      context: {
        includePlatformLevelData: true,
      },
    },
  );

  return {
    conversationStatusList: data?.getConversationStatus.records,
    ...rest,
  };
};

export const statusDisplayPropsMap = {
  new: { icon: 'flare', color: 'custom.OV.sys.primary' },
  open: { icon: 'play_circle', color: 'custom.OV.sys.primary' },
  snoozed: { icon: 'snooze', color: 'custom.OV.sys.secondary' },
  on_hold: { icon: 'pause_circle', color: 'custom.OV.sys.secondary' },
  solved: { icon: 'check_circle', color: 'custom.OV.sys.tertiary' },
  closed: { icon: 'lock', color: 'custom.OV.sys.tertiary' },
  default: { icon: 'circle', color: 'custom.OV.sys.onSurface' },
} as const;

export type StatusId = keyof typeof statusDisplayPropsMap;

export type ConversationStatusOption = Pick<
  ConversationStatus,
  'id' | 'sortOrder' | 'name'
> & {
  label: ConversationStatus['name'];
  value: ConversationStatus['id'];
  icon: string;
  color: string;
};

export const useConversationStatusOptions = (
  options?: QueryHookOptions<GetConversationStatusQuery>,
) => {
  const { conversationStatusList } = useConversationStatusList(options);
  const theme = useTheme();

  return useMemo<ConversationStatusOption[]>(() => {
    if (!conversationStatusList?.length) {
      return [];
    }

    return conversationStatusList.map((item) => {
      return {
        id: item.id,
        value: item.id,
        name: item.name,
        label: item.name,
        sortOrder: item.sortOrder,
        icon:
          statusDisplayPropsMap[item.id as StatusId].icon ||
          statusDisplayPropsMap.default.icon,
        color: getStatusColor(item.id as StatusId, theme),
      };
    });
  }, [conversationStatusList, theme]);
};

export function getStatusColor(item: StatusId, theme: Theme): string {
  return getThemeColor(statusDisplayPropsMap[item].color, theme);
}

export const DEFAULT_STATUS = {
  id: 'default',
  sortOrder: 0,
  value: 'default',
  label: 'Default',
  name: 'Default',
  icon: 'circle',
  color: 'inherit',
} satisfies ConversationStatusOption;
